import {
  ApiError,
  type Activity,
  type ActivityGap,
  type Assessment,
  type AskResponse,
  type Attachment,
  type Contact,
  type StreamStep,
  type Conversation,
  type ConversationDetail,
  type DocumentDetail,
  type DraftDetail,
  type DraftSummary,
  type FactCategory,
  type HistoryEntry,
  type Memory,
  type Question,
  type Rating,
  type TemplateDetail,
  type TemplateSave,
  type TemplateSource,
  type TemplateSummary,
  type UploadTicket,
  type User,
} from "./types";

/**
 * The session lives in an httpOnly cookie set by the backend, so there is no
 * token in JavaScript for a cross-site script to steal and nothing to
 * refresh by hand. Same-origin in both environments - CloudFront in
 * production, the Vite proxy locally - so the cookie is sent automatically.
 */
async function request<T>(
  path: string,
  init: RequestInit = {},
): Promise<T> {
  let res: Response;
  try {
    res = await fetch(`/api${path}`, {
      credentials: "same-origin",
      headers: { "Content-Type": "application/json", ...(init.headers ?? {}) },
      ...init,
    });
  } catch {
    // The pilot pauses by stopping the instance, so an unreachable backend
    // is an expected state and deserves a real sentence, not "Failed to fetch".
    throw new ApiError(0, "Serviciul nu este disponibil. Încercați mai târziu.");
  }

  if (res.status === 204) return undefined as T;

  const body = await res.json().catch(() => null);
  if (!res.ok) {
    throw new ApiError(
      res.status,
      (body && (body.detail ?? body.message)) || `Eroare ${res.status}`,
    );
  }
  return body as T;
}

/**
 * Save a file the server generates. Not `request`, which parses JSON: this
 * is a binary body, and the filename the server chose is in
 * Content-Disposition.
 */
async function download(url: string, fallbackName: string): Promise<void> {
  let res: Response;
  try {
    res = await fetch(url, { credentials: "same-origin" });
  } catch {
    throw new ApiError(0, "Serviciul nu este disponibil. Încercați mai târziu.");
  }
  if (!res.ok) {
    throw new ApiError(res.status, "Exportul a eșuat.");
  }

  const disposition = res.headers.get("content-disposition") ?? "";
  const match = /filename="([^"]+)"/.exec(disposition);
  saveBlob(await res.blob(), match?.[1] ?? fallbackName);
}

/** Hand the browser a blob to save under `name`. */
export function saveBlob(blob: Blob, name: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = name;
  document.body.appendChild(link);
  link.click();
  link.remove();
  // Revoked on the next tick: revoking synchronously can cancel the
  // download in some browsers before it has read the blob.
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

/**
 * Read an answer stream to its end: `started`, the steps, then `done` or
 * `error`. Shared by asking and resuming, which follow the same run.
 */
async function readAnswer(
  res: Response,
  onStep: (step: StreamStep) => void,
  onStarted?: (conversationId: string) => void,
): Promise<AskResponse> {
  if (!res.ok || !res.body) {
    // Everything that can refuse the request does so before the stream
    // opens, so a non-2xx here is an ordinary JSON error.
    const body = await res.json().catch(() => null);
    throw new ApiError(
      res.status,
      (body && (body.detail ?? body.message)) || `Eroare ${res.status}`,
    );
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  let answer: AskResponse | null = null;
  let failure: string | null = null;

  // SSE frames are separated by a blank line. A chunk can split one, so
  // the tail stays in the buffer until its terminator arrives.
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });

    const SEP = "\n\n";
    const NL = "\n";
    let cut: number;
    while ((cut = buffer.indexOf(SEP)) !== -1) {
      const frame = buffer.slice(0, cut);
      buffer = buffer.slice(cut + 2);

      let event = "message";
      const data: string[] = [];
      for (const line of frame.split(NL)) {
        if (line.startsWith("event: ")) event = line.slice(7).trim();
        else if (line.startsWith("data: ")) data.push(line.slice(6));
      }
      if (!data.length) continue;
      const payload = JSON.parse(data.join(NL));

      if (event === "started") onStarted?.(payload.conversation_id);
      else if (event === "step") onStep(payload as StreamStep);
      else if (event === "done") answer = payload as AskResponse;
      else if (event === "error") failure = payload.message;
    }
  }

  if (failure) throw new ApiError(502, failure);
  if (!answer) {
    // The connection closed without a terminal event. The answer goes on
    // on the server and is stored when it is done.
    throw new ApiError(
      0,
      "Conexiunea s-a întrerupt. Reîncărcați conversația pentru a vedea răspunsul.",
    );
  }
  return answer;
}

export const api = {
  me: () => request<User>("/auth/me"),

  login: (email: string, password: string) =>
    request<User>("/auth/login", {
      method: "POST",
      body: JSON.stringify({ email, password }),
    }),

  logout: () => request<void>("/auth/logout", { method: "POST" }),

  consent: () => request<User>("/auth/consent", { method: "POST" }),

  /**
   * The same question, with the agent's steps arriving as they happen.
   *
   * fetch + a stream reader rather than EventSource, which only does GET and
   * cannot send the question. `onStep` fires per tool call - twice per tool,
   * once running and once done, keyed by `id` so the caller updates in place
   * rather than appending duplicates. `onStarted` fires first, with the
   * conversation's id, so a new thread has its URL before its answer.
   *
   * The answer runs on the server whatever happens to this stream: a reload
   * or a dropped connection stops the watching, not the answer, and
   * `resume` picks it up again.
   */
  askStream: async (
    question: string,
    conversationId: string | null,
    onStep: (step: StreamStep) => void,
    attachmentIds: string[] = [],
    onStarted?: (conversationId: string) => void,
  ): Promise<AskResponse> => {
    let res: Response;
    try {
      res = await fetch("/api/ask/stream", {
        method: "POST",
        credentials: "same-origin",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          question,
          conversation_id: conversationId,
          attachment_ids: attachmentIds,
        }),
      });
    } catch {
      throw new ApiError(0, "Serviciul nu este disponibil. Încercați mai târziu.");
    }
    return readAnswer(res, onStep, onStarted);
  },

  /**
   * Follow the answer still being written in a thread, from its first step.
   * For a page reloaded mid-answer. Null when nothing is running: the answer
   * is already stored, or a restart lost it.
   */
  resume: async (
    conversationId: string,
    onStep: (step: StreamStep) => void,
    signal?: AbortSignal,
  ): Promise<AskResponse | null> => {
    let res: Response;
    try {
      res = await fetch(`/api/conversations/${conversationId}/run`, {
        credentials: "same-origin",
        signal,
      });
    } catch (e) {
      if (signal?.aborted) throw e;
      throw new ApiError(0, "Serviciul nu este disponibil. Încercați mai târziu.");
    }
    if (res.status === 204) return null;
    return readAnswer(res, onStep);
  },

  ask: (question: string, conversationId: string | null) =>
    request<AskResponse>("/ask", {
      method: "POST",
      body: JSON.stringify({ question, conversation_id: conversationId }),
    }),

  /** The whole act behind a citation, for checking it in context. */
  document: (docId: string) => request<DocumentDetail>(`/documents/${docId}`),

  /**
   * Upload a file in three steps: ask for a ticket, send the bytes where it
   * says, then tell the API they landed.
   *
   * The middle step usually goes straight to S3, so the file never transits
   * the API box — which is one instance, and would otherwise spend its disk
   * and uplink on bytes that never needed to touch it. The size limit is
   * enforced by the bucket itself through the POST policy, so an oversized
   * file is refused by S3 rather than after we have already stored it.
   *
   * Returns the attachment once the server has read it. A file that could
   * not be read comes back with `status: "failed"` and a reason rather than
   * throwing: the chip is already on screen, and what it needs is a
   * sentence to show, not an exception that loses which file it was about.
   */
  upload: async (
    file: File,
    conversationId: string | null,
  ): Promise<Attachment> => {
    const ticket = await request<UploadTicket>("/uploads", {
      method: "POST",
      body: JSON.stringify({
        filename: file.name,
        content_type: file.type,
        size_bytes: file.size,
        conversation_id: conversationId,
      }),
    });

    const form = new FormData();
    // The policy fields have to precede the file, which S3 requires and
    // which FormData preserves in insertion order.
    for (const [key, value] of Object.entries(ticket.fields)) {
      form.append(key, value);
    }
    form.append("file", file);

    let put: Response;
    try {
      put = await fetch(ticket.url, {
        method: "POST",
        body: form,
        // No credentials to S3: the presigned policy is the authorisation,
        // and sending the session cookie cross-origin would fail CORS.
        credentials: ticket.mode === "s3" ? "omit" : "same-origin",
      });
    } catch {
      throw new ApiError(0, "Încărcarea fișierului a eșuat. Încercați din nou.");
    }
    if (!put.ok) {
      throw new ApiError(
        put.status,
        put.status === 413
          ? "Fișierul este prea mare."
          : "Încărcarea fișierului a eșuat. Încercați din nou.",
      );
    }

    return request<Attachment>(
      `/uploads/${ticket.attachment_id}/complete`,
      { method: "POST" },
    );
  },

  deleteUpload: (id: string) =>
    request<void>(`/uploads/${id}`, { method: "DELETE" }),

  conversations: () => request<Conversation[]>("/conversations"),

  conversation: (id: string) =>
    request<ConversationDetail>(`/conversations/${id}`),

  deleteConversation: (id: string) =>
    request<void>(`/conversations/${id}`, { method: "DELETE" }),

  rate: (messageId: string, rating: Rating) =>
    request<void>(`/messages/${messageId}/rating`, {
      method: "PUT",
      body: JSON.stringify(rating),
    }),

  /* --------------------------------------------------------- drafting */

  templates: () => request<TemplateSummary[]>("/templates"),

  template: (slug: string) => request<TemplateDetail>(`/templates/${slug}`),

  templateSource: (slug: string) =>
    request<TemplateSource>(`/templates/${encodeURIComponent(slug)}/source`),

  /** A copy in the user's own templates, to edit freely. */
  copyTemplate: (slug: string) =>
    request<TemplateSummary>(`/templates/${encodeURIComponent(slug)}/copy`, {
      method: "POST",
    }),

  /** One of the user's own; refused while documents use it. */
  deleteTemplate: (slug: string) =>
    request<void>(`/templates/${encodeURIComponent(slug)}`, { method: "DELETE" }),

  /** Saved as a new version: a copy by its owner, the shared library by an
   *  admin. Existing drafts keep the version they started from. */
  saveTemplate: (slug: string, body: TemplateSave) =>
    request<TemplateSource>(`/templates/${encodeURIComponent(slug)}`, {
      method: "PUT",
      body: JSON.stringify(body),
    }),

  /**
   * Drafts live under /drafts, not /documents: `/documents/{id}` already
   * means an act from the corpus in this API, and overloading it would
   * make a draft unreachable behind the act lookup.
   */
  drafts: () => request<DraftSummary[]>("/drafts"),

  draft: (id: string) => request<DraftDetail>(`/drafts/${id}`),

  createDraft: (templateSlug: string, title: string) =>
    request<DraftDetail>("/drafts", {
      method: "POST",
      body: JSON.stringify({ template_slug: templateSlug, title }),
    }),

  /**
   * Partial: only the keys sent are touched, and an empty value clears the
   * field rather than storing a blank — so a cleared field goes back to
   * being reported as missing instead of counting as answered.
   *
   * Returns the whole re-rendered draft, so the caller never has to guess
   * what a value changed downstream.
   */
  setValues: (id: string, values: Record<string, string>) =>
    request<DraftDetail>(`/drafts/${id}/values`, {
      method: "PUT",
      body: JSON.stringify({ values }),
    }),

  editClause: (id: string, clauseKey: string, text: string) =>
    request<DraftDetail>(`/drafts/${id}/clauses/${clauseKey}`, {
      method: "PUT",
      body: JSON.stringify({ text }),
    }),

  /** Put a field added in this draft into one of its form sections. */
  placeField: (id: string, key: string, section: string) =>
    request<DraftDetail>(`/drafts/${id}/fields/${encodeURIComponent(key)}`, {
      method: "PUT",
      body: JSON.stringify({ section }),
    }),

  revertClause: (id: string, clauseKey: string) =>
    request<DraftDetail>(`/drafts/${id}/clauses/${clauseKey}`, {
      method: "DELETE",
    }),

  renameDraft: (id: string, title: string) =>
    request<DraftDetail>(`/drafts/${id}`, {
      method: "PATCH",
      body: JSON.stringify({ title }),
    }),

  deleteDraft: (id: string) =>
    request<void>(`/drafts/${id}`, { method: "DELETE" }),

  /** .docx to edit in Word, .pdf to send. Both carry the draft marking. */
  exportDraft: (id: string, format: "docx" | "pdf" = "docx") =>
    download(`/api/drafts/${id}/export?format=${format}`, `document-proiect.${format}`),

  /** The whole document from the editor; returns the draft re-read from it
   *  (fields, gaps and sections follow the text). */
  saveContent: (id: string, content: Record<string, unknown>) =>
    request<DraftDetail>(`/drafts/${id}/content`, {
      method: "PUT",
      body: JSON.stringify({ content }),
    }),

  /** Back to the template's wording; the field values stay. */
  resetContent: (id: string) =>
    request<DraftDetail>(`/drafts/${id}/content`, { method: "DELETE" }),

  // ------------------------------------------- data-protection compliance

  /** The questionnaire, already filtered to what this business must answer. */
  complianceQuestions: () => request<Question[]>("/compliance/questions"),

  /**
   * Save answers and get the reassessment in the same round trip.
   *
   * Partial on purpose: send only what changed. An omitted field keeps its
   * stored answer, while an explicit null clears it.
   */
  saveProfile: (answers: Record<string, boolean | number | string | null>) =>
    request<Assessment>("/compliance/profile", {
      method: "PUT",
      body: JSON.stringify(answers),
    }),

  assessment: () => request<Assessment>("/compliance/assessment"),

  setObligation: (
    code: string,
    patch: { state?: string; evidence_ref?: string; determination?: string },
  ) =>
    request<Assessment>(`/compliance/obligations/${code}`, {
      method: "PUT",
      body: JSON.stringify(patch),
    }),

  register: () => request<Activity[]>("/compliance/register"),

  registerGaps: () => request<ActivityGap[]>("/compliance/register/gaps"),

  createActivity: (body: Partial<Activity>) =>
    request<Activity>("/compliance/register", {
      method: "POST",
      body: JSON.stringify(body),
    }),

  updateActivity: (id: string, body: Partial<Activity>) =>
    request<Activity>(`/compliance/register/${id}`, {
      method: "PUT",
      body: JSON.stringify(body),
    }),

  deleteActivity: (id: string) =>
    request<void>(`/compliance/register/${id}`, { method: "DELETE" }),

  contacts: () => request<Contact[]>("/compliance/contacts"),

  saveContact: (body: Omit<Contact, "id">) =>
    request<Contact[]>("/compliance/contacts", {
      method: "PUT",
      body: JSON.stringify(body),
    }),

  /** The register as .docx, for the Centre (art. 30 alin. (4)). */
  exportRegister: () =>
    download("/api/compliance/register/export", "evidenta-art-30.docx"),

  /** Every wording a provision has had, for "Istoricul versiunilor". */
  history: (familyId: string, path: string | null) =>
    request<HistoryEntry[]>(
      `/history/${encodeURIComponent(familyId)}` +
        (path ? `?path=${encodeURIComponent(path)}` : ""),
    ),

  // ------------------------------------------------------------- memory
  // Every call returns the whole memory: an edit to a profile-backed fact
  // moves the compliance profile, and the screen should not guess what else
  // changed.

  memory: () => request<Memory>("/memory"),

  addFact: (body: { category: FactCategory; label: string; value: string }) =>
    request<Memory>("/memory/facts", {
      method: "POST",
      body: JSON.stringify(body),
    }),

  updateFact: (id: string, value: string) =>
    request<Memory>(`/memory/facts/${encodeURIComponent(id)}`, {
      method: "PATCH",
      body: JSON.stringify({ value }),
    }),

  deleteFact: (id: string) =>
    request<Memory>(`/memory/facts/${encodeURIComponent(id)}`, {
      method: "DELETE",
    }),

  /** Confirm the given proposals, or all of them when `ids` is omitted. */
  confirmFacts: (ids?: string[]) =>
    request<Memory>("/memory/facts/confirm", {
      method: "POST",
      body: JSON.stringify({ ids: ids ?? null }),
    }),

  forgetFile: (attachmentId: string) =>
    request<Memory>(`/memory/files/${attachmentId}`, { method: "DELETE" }),

  setMemoryEnabled: (enabled: boolean) =>
    request<Memory>("/memory/settings", {
      method: "PATCH",
      body: JSON.stringify({ enabled }),
    }),
};
